import { use, useMemo } from "react";
import { CompanyAccountingYearContext } from "@domain/context/CompanyContext";
import { ReadinessContext } from "@domain/context/DataReadyness";
import { useBWAAccountMap } from "@accounting/common/overviews/BWA/hooks/useBWAAccountMap";
import { computeBwaGroups, type BwaResultRow } from "./bwaGroups";

export type MetricStatus = "loading" | "empty" | "ready";

export interface GuVMetrics {
    status: MetricStatus;
    /** cents, positive = revenue (Umsatzerlöse, BWA row 1020) */
    revenue: number;
    /** cents, positive = costs (Gesamtkosten, BWA row 1280, sign-flipped for display) */
    costs: number;
    /** cents, positive = profit (vorläufiges Ergebnis, BWA row 1380) */
    profit: number;
    /** cents, positive = profit (Betriebsergebnis, BWA row 1300 = betrieblicher Rohertrag − Gesamtkosten) */
    operatingResult: number;
    /** 12 entries, index 0 = January, cents */
    monthlyRevenue: number[];
    monthlyCosts: number[];
}

const EMPTY_MONTHS = new Array(12).fill(0);
const ZERO: Omit<GuVMetrics, "status"> = {
    revenue: 0,
    costs: 0,
    profit: 0,
    operatingResult: 0,
    monthlyRevenue: EMPTY_MONTHS,
    monthlyCosts: EMPTY_MONTHS,
};

type RowLike = Pick<BwaResultRow, "key" | "periodAmounts">;

export const selectGuV = (rows: RowLike[]): Omit<GuVMetrics, "status"> => {
    const byKey = new Map(rows.map((r) => [r.key, r]));
    const sumAll = (row?: RowLike) => (row ? Array.from(row.periodAmounts.values()).reduce((acc, v) => acc + v, 0) : 0);
    const monthly = (row?: RowLike) => EMPTY_MONTHS.map((_, i) => row?.periodAmounts.get(i + 1) ?? 0);
    const revenueRow = byKey.get(1020);
    const costsRow = byKey.get(1280);
    const profitRow = byKey.get(1380);
    const operatingResultRow = byKey.get(1300);
    // BWA sign convention (after invertSign): revenue positive, costs negative → flip for display.
    // `|| 0` normalizes -0, which Intl would otherwise render as "-0,00".
    return {
        revenue: sumAll(revenueRow),
        costs: -sumAll(costsRow) || 0,
        profit: sumAll(profitRow),
        operatingResult: sumAll(operatingResultRow),
        monthlyRevenue: monthly(revenueRow),
        monthlyCosts: monthly(costsRow).map((v) => -v || 0),
    };
};

export const useGuVMetrics = (): GuVMetrics => {
    const { isReady } = use(ReadinessContext);
    const { yearConfig } = use(CompanyAccountingYearContext);
    const bwaData = useBWAAccountMap();
    return useMemo(() => {
        if (!isReady || !yearConfig) {
            return { status: "loading" as const, ...ZERO };
        }
        // useBWAAccountMap returns an empty map when there are no records
        if (bwaData.size === 0) {
            return { status: "empty" as const, ...ZERO };
        }
        return { status: "ready" as const, ...selectGuV(computeBwaGroups(bwaData, yearConfig.skr)) };
    }, [isReady, yearConfig, bwaData]);
};
